import React, { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import "header-pattern/dist/main-ds1-stopgap.css";
import "./Header.css";

import { authService } from "../../services/auth/authService";
import { getFilteredHeaderNavigations, Navigation } from "../../config/headerNavigations";
import { useAppSelector } from "../../store/hooks";

import UserProfile from "./UserProfile";
import NavItem from "./NavItem";
import { useEmployee } from "../../contexts/EmployeeContext";
import Loader from "../common/Loader";

const Header: React.FC = () => {
  const handleSignOff = useCallback(async () => {
    await authService.logout();
  }, []);
  const { employeeDetails, loadingEmployee } = useEmployee();
  const primaryRole = useAppSelector((state) => state.profile.profile?.primaryRole ?? "");
  const profileLoading = useAppSelector((state) => state.profile.loading);
  const visibleNavigations = useMemo(() => getFilteredHeaderNavigations(primaryRole), [primaryRole]);

  const firstName = useMemo(() => {
    return `${employeeDetails?.firstName ?? ""}`.trim();
  }, [employeeDetails]);

  return (
    <header
      id="kp-header"
      className="kp-header authenticated universal exposed-search theme-nt-mobile-inky-blue nav-tray--initialized nav-tray--0"
      data-analytics-location="kp-header"
    >
      <div className="kp-header__dim" />

      {/* Mobile */}
      <div className="kp-header__top-header">
        <div className="kp-header__rows-container">
          <div className="kp-header__branding-mobile col-span-2 md:col-span-4">
            <div className="primary screen-only">
              <Link
                to="/"
                role="img"
                aria-label="Kaiser Permanente Home"
                data-analytics-type="image"
                data-analytics-click="kp-logo"
              >
                <img
                  className="logo-icon"
                  src="/assets/images/kp-logo-signature-stacked.svg"
                  width="150"
                  height="40"
                  alt=""
                  aria-hidden="true"
                />
              </Link>
            </div>
          </div>

          <div className="kp-header__mobile-buttons col-span-4! md:col-span-8!">
            <UserProfile
              userName={firstName}
              onSignOff={handleSignOff}
              loadingEmployee={loadingEmployee}
            />
          </div>
        </div>
      </div>

      {/* Desktop / Tablet */}
      <div
        className="kp-header-global-menu pushdown-nav"
        id="kp-header-global-menu"
      >
        <div className="kp-header-global-menu__navigation-outer-container lg-laptop:max-w-none! lg-laptop:mx-40! lg-laptop:px-0!">
          <span className="screenreader-only device-only">
            Beginning of navigation menu
          </span>

          <div className="kp-header__middle-bar">
            <div className="kp-header__branding">
              <div className="primary screen-only">
                <Link
                  to="/"
                  role="img"
                  aria-label="Kaiser Permanente Home"
                  data-analytics-type="image"
                  data-analytics-click="kp-logo"
                >
                  <img
                    className="logo-long"
                    src="/assets/images/KP_Signature_white_0817.png"
                    width="374"
                    height="42"
                    alt=""
                    aria-hidden="true"
                  />
                </Link>
              </div>
            </div>
            <div className="kp-header__authenticated-search-desktop">
              <UserProfile
                userName={firstName}
                onSignOff={handleSignOff}
                loadingEmployee={loadingEmployee}
              />
            </div>
          </div>

          <div className="kp-header__bottom-bar">
            <div className="kp-header-global-menu__inner">
              <div className="kp-header-global-menu__navigation-container navigation-tray-pattern">
                <div
                  id="logged_in_topnav"
                  className="swap-position"
                  data-device-position="nav-device-position-authenticated"
                  data-ref="1"
                >
                  <nav aria-label="UBT Navigation">
                    <ul className="kp-header-global-menu__primary-links-list unstyled-list">
                      {profileLoading ? (
                        <li><Loader /></li>
                      ) : (
                        visibleNavigations.map((navigation: Navigation) => {
                          const key = `${navigation.title}-${navigation.link ?? "menu"}`;
                          return <NavItem navigation={navigation} key={key} />;
                        })
                      )}
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>

        <span className="screenreader-only device-only kp-header__menu-end">
          End of navigation menu
        </span>
        <span className="kp-header__menu-end-trigger" />
      </div>
    </header>
  );
};

export default Header;
